import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { User } from 'BaseDataTypes';
import {
  Course,
  CourseParticipant,
  UserParticipant,
  CourseProgress,
  CourseProgressNameType,
  SelectableCourseProgressNameType,
  CourseProgressTranslations,
  certificateFile,
} from 'TrainingTrackingDataTypes';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { dialogData, genericDialogComponent } from '../genericDialog';
import { CourseParticipantsService } from 'TrainingTrackingServices/course-participants-service';
import { CourseService } from 'TrainingTrackingServices/course-service';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';

import {
  MatDialogModule,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { UserService } from 'BaseServices/user-service';
import { dialogInputTitleComponent } from '../../../../../../src/app/elements/dialog-input-title.component';
import { DeleteButtonComponent } from '../../../../../../src/app/buttons/delete-button/delete-button.component';
import { SaveButtonComponent } from '../../../../../../src/app/buttons/save-button/save-button.component';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-course-participants-dialog',
    imports: [
        CommonModule,
        FormsModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatNativeDateModule,
        MatInputModule,
        MatDialogModule,
        MatButtonModule,
        dialogInputTitleComponent,
        DeleteButtonComponent,
        SaveButtonComponent,
    ],
    templateUrl: './course-participants-dialog.component.html',
    styleUrl: './course-participants-dialog.component.css',
    providers: []
})
export class CourseParticipantsDialogComponent
  extends genericDialogComponent
  implements OnInit
{
  readonly data = inject<dialogData<CourseParticipant[]>>(MAT_DIALOG_DATA);
  @Input() participant!: CourseParticipant;
  @Input() Users!: User;
  @Input() course!: Course;
  @Input() courseParticipant: CourseProgress[] = [];

  @ViewChild('fileUpload') fileUpload!: ElementRef;

  allCourses: Course[] = [];
  allUsers: UserParticipant[] = [];
  searchedUsers: UserParticipant[] = [];
  searchedUserString: string = '';
  currentUser = this.userService.currentUser;
  currentStatusId: number = 0;
  historicStatusId: number = 0;
  selectedProgress!: CourseProgress;
  courseProgressNameType = CourseProgressNameType;
  selectableCourseProgressNameType = SelectableCourseProgressNameType;
  courseProgressTranslations = CourseProgressTranslations;
  courseProgressIsExpired: boolean = false;
  participantsSelected: CourseParticipant[] = [];
  multiEditMode: boolean = false;
  loadNewParticipantProgressOnClose: boolean = false;

  constructor(
    public override dialogRef: MatDialogRef<CourseParticipantsDialogComponent>,
    private courseParticipantService: CourseParticipantsService,
    public userService: UserService,
    private courseService: CourseService
  ) {
    super(dialogRef);
    this.participant = this.data.data[0];
    this.multiEditMode = this.data.data.length > 1;
    this.dialogRef.backdropClick().subscribe(() => {
      this.closeDialog();
    });
  }
  ngOnInit(): void {
    if (this.participant?.courseProgresses?.length) {
      this.selectedProgress = this.participant?.courseProgresses[0];
      this.currentStatusId = this.selectedProgress.id;
      this.isCourseProgressExpired();
    }

    //Gets all users that current user can create participations for, and sets them as not participating by default. If the user has the role "User", they are set as participating from the start.
    this.userService.getUsersForCourseParticipant().subscribe((result) => {
      for (let user of result) {
        this.allUsers.push({
          ...user,
          participating: false,
        } as UserParticipant);
      }
      // If the users role is User, we wish to set them as participating from the start.
      if (
        this.userService.getSelectedRole ==
          this.userService.getUserRoleTypes.User 
      ) {
        const currentUserParticipant = this.allUsers.find(u => u.id === this.userService.currentUser?.id);
        if (currentUserParticipant) {
          this.refreshParticipating(currentUserParticipant);
        }
        
      } else if (this.participant.id != 0) {
        this.participantsSelected.push(this.participant);
      }

      for (let i = 0; i < this.participant.courseProgresses.length; i++) {
        this.participant.courseProgresses[i].deadline = new Date(
          this.participant.courseProgresses[i].deadline
        );
      }
    });

    //Gets all courses
    this.courseService.getCourses().subscribe((result) => {
      this.allCourses = result;
    });
    this.searchedUsers = this.allUsers;
  }

  /**
   * Checks if we are editing multiple participants
   * @returns
   */
  isEditingMultipleParticipants(): boolean {
    return this.multiEditMode || this.participantsSelected.length > 1;
  }

  /**
   * Function to refresh the participating status of a user
   */
  refreshParticipating(participant: UserParticipant) {
    if (!participant.participating) {
      let newParticipant = structuredClone(this.participant); // Clone the base participant
      newParticipant.userId = participant.id; // Update userId with the current user's ID
      this.participantsSelected.push(newParticipant);
    } else {
      this.participantsSelected = this.participantsSelected.filter(
        (p) => p.userId !== participant.id
      );
    }

    participant.participating = !participant.participating;
  }

  /**
   * Are we allowed to save a participant
   * @returns boolean
   */
  canSaveParticipant(): boolean {
    if (this.selectedProgress.id != this.participant.courseProgresses[0].id)
      return true;

    return (
      this.participant.courseId == 0 ||
      this.participantsSelected.length == 0 ||
      (this.selectedProgress.courseProgressName ==
        this.courseProgressNameType.Completed &&
        !this.selectedProgress.completionDate) ||
      (this.selectedProgress.courseProgressName ==
        this.courseProgressNameType.Enrolled &&
        !this.selectedProgress.deadline)
    );
  }

  deleteCourseProgressButtonText(): string {
    if (
      this.participant.courseProgresses &&
      this.participant.courseProgresses.length > 1 &&
      !this.multiEditMode
    )
      return 'Slet valgte tilmelding';
    return 'Slet tilmelding';
  }

  /**
   * Serching for user based on changes made in textfield value.
   * @param event
   */
  searchForUser(event: any): void {
    this.searchedUserString = event.target.value;
    var listOfSearches = this.searchedUserString.toLowerCase().split(' ');
    //searchedUsers is a list of all users after filtering for the value of the textfield (searchedUserString)
    this.searchedUsers = this.allUsers.filter((user) =>
      listOfSearches.every((searchTerm) =>
        user.name.toLowerCase().includes(searchTerm)
      )
    );
  }

  /**
   * Updates the expiry date of the participant's course.
   *
   * This method retrieves the course ID from the participant, converts it to a number if necessary,
   * and then finds the corresponding course from the list of all courses. If the course is found,
   * it sets the expiry date based on the course's validity period.
   *
   * @returns {void}
   */
  onCourseChanged() {
    let courseId = this.participant.courseId;
    if (typeof courseId == 'string') {
      courseId = parseInt(courseId);
    }
    // Find the course using participant.course?.id
    const selectedCourse = this.allCourses.find(
      (course) => course.id === courseId
    );

    // Calculate if the course has an expiry date
    if (selectedCourse?.yearsValid != 0) {
      var completionDate = this.selectedProgress.completionDate;
      if (completionDate) {
        const newExpiryDate = new Date(completionDate);
        newExpiryDate.setFullYear(
          newExpiryDate.getFullYear() + selectedCourse!.yearsValid
        );
        this.selectedProgress.expiryDate = newExpiryDate;
      }
    } else {
      this.selectedProgress.expiryDate = undefined;
    }

    if (!selectedCourse) {
      console.error(
        'Selected course not found in allCourses:',
        this.participant.course?.id
      );
    }
  }

  /**
   * Updates wether a course progress is expired or not.
   */
  isCourseProgressExpired() {
    this.courseProgressIsExpired =
      this.selectedProgress.courseProgressName ==
      this.courseProgressNameType.Expired;
  }

  /**
   * Uploads a certificate by providing the ID of a specific status and the FormData of the file uploaded.
   * @param statusId
   * @returns
   */
  tryToUploadCertificate(statusId: number): boolean {
    if (!this.fileUpload) {
      return false;
    }
    const file: File = this.fileUpload.nativeElement.files[0];
    //upload file if there is a file
    if (file && statusId != -1) {
      const formData = new FormData();
      formData.append('file', file, file.name);

      this.courseParticipantService
        .uploadCertificate(statusId, formData)
        .subscribe(() => {
          this.dialogRef.close(true);
          this.selectedProgress.certificateContentType = file.type;
        });
      return true;
    } else {
      return false;
    }
  }

  /**
   * Deletes either 1 participant status or many depending on how many participants are selected, using the deleteNewestStatus service
   * @returns
   */
  deleteParticipation() {
    if (!confirm('Er du sikker på at du vil slette tilmeldningen?')) {
      return;
    }
    let deleteRequests;
    //If more than one participants are selected using "Rediger flere" button
    if (this.multiEditMode) {
      deleteRequests = this.data.data.map((participant) =>
        this.courseParticipantService.deleteCourseProgress(
          participant.courseProgresses[0].id
        )
      );
    } else {
      deleteRequests = [
        this.courseParticipantService.deleteCourseProgress(
          this.selectedProgress.id
        ),
      ];
    }
    forkJoin(deleteRequests).subscribe(() => {
      this.dialogRef.close(true);
    });
  }

  override closeDialog() {
    this.dialogRef.close(this.loadNewParticipantProgressOnClose);
  }

  /**
   * Deletes all progress history for the selected participant.
   */
  deleteHistory() {
    if (!confirm('Er du sikker på at du vil slette hele kursus historikken?')) {
      return;
    }

    for (let participant of this.data.data) {
      this.courseParticipantService
        .deleteAllHistory(participant.id)
        .subscribe(() => {
          this.dialogRef.close(true);
        });
    }
  }

  /**
   * Håndterer klik på en historikknap og opdaterer den valgte status.
   */
  onStatusChange(progress: CourseProgress): void {
    this.selectedProgress = { ...progress }; // We need to make a shallow copy, hence the dots and brackets
    this.isCourseProgressExpired();
  }

  /**
   * Uploads file on click.
   */
  uploadFile() {
    this.fileUpload.nativeElement.click();
  }

  /**
   * Function to store the selected file and updating the name of the file when selected.
   * @param event
   */
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      let selectedFile = input.files[0]; 
      this.selectedProgress.certificateContentType = selectedFile.type;
      this.saveFile(selectedFile);
    }
  }

  saveFile(selectedFile: File) {
    if (selectedFile) {
      const formData = new FormData();
      formData.append('file', selectedFile);
      this.courseParticipantService
        .uploadCertificate(this.selectedProgress.id, formData)
        .subscribe(() => {
          this.loadNewParticipantProgressOnClose = true;
        });
    }
  }

  /**
   * Button to download file that is being stored in a specific participationStatus.
   */
  downloadFile() {
    if (this.selectedProgress.certificateContentType != null) {
      let selectedFile: File | undefined;
      this.courseParticipantService
        .requestCertificate(this.selectedProgress.id)
        .subscribe((file: certificateFile) => {
          if (!file || !file.certificate || !file.certificateContentType) {
            return;
          }

          // Convert base64 to Uint8Array
          const binaryString = window.atob(file.certificate);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          // Create a Blob from the Uint8Array
          const blob = new Blob([bytes], { type: file.certificateContentType });

          // Store the file in `selectedFile` for later use
          selectedFile = new File([blob], 'Certificate', {
            type: file.certificateContentType,
            lastModified: new Date().getTime(),
          });
          if (selectedFile == undefined) return;

          const url = URL.createObjectURL(selectedFile);
          const a = document.createElement('a');
          a.href = url;
          a.download = selectedFile.name; // Set the filename for download
          document.body.appendChild(a);
          a.click(); // Trigger the download
          document.body.removeChild(a);
          URL.revokeObjectURL(url); // Clean up
        });
    }
  }

  /**
   * Deletes certificate from the database using the deleteCertificate service
   */
  deleteCertificate() {
    if (!confirm('Er du sikker på at du vil slette certifikatet?')) {
      return;
    }
    // delete certificate for the selected participant
    this.courseParticipantService
      .deleteCertificate(this.selectedProgress.id)
      .subscribe(() => {
        this.loadNewParticipantProgressOnClose = true;
        this.selectedProgress.certificateContentType = undefined;
      });
  }
  /**
   * Saves changes to course participant data based on the current state and updates the database.
   * - Adjusts deadlines and completion dates based on timezone.
   * - Handles logic for multiple participants and updating or adding them to the course.
   * - Uploads certificates if applicable.
   * @returns void
   */
  saveChanges() {
    //Edit multiple mode
    if (this.multiEditMode) {
      for (let i = 1; i < this.data.data.length; i++) {
        let multiSelectedParticipant = this.data.data[i];
        this.participantsSelected.push(multiSelectedParticipant);
      }
    }
    this.participantsSelected.forEach((participant) => {
      participant.courseProgresses[0].courseProgressName =
        this.selectedProgress.courseProgressName;
      participant.courseProgresses[0].completionDate =
        this.selectedProgress.completionDate;
      participant.courseProgresses[0].deadline = this.selectedProgress.deadline;

      if (!this.multiEditMode) {
        participant.courseId = this.participant.courseId;
      }
    });

    // Update all participants in the backend
    this.courseParticipantService
      .upsertParticipants(this.participantsSelected)
      .subscribe((result) => {
        if (result.length == 1 && this.participantsSelected.length == 1) {
          const courseProgress = result[0];
          if (courseProgress == null) {
            return;
          }
          let statusId = courseProgress.courseProgresses[0].id;
          this.tryToUploadCertificate(statusId);
        }
        this.dialogRef.close(true);
      });
  }

  @HostListener('window:keyup', ['$event'])
  closeByEscape(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeDialog();
    }
  }
}
